import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, Navigate, Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { AlertTriangle, Bell, BookOpen, ChevronDown, Megaphone, ChevronRight, ClipboardList, LogOut, Menu, MessageSquare, PanelLeftClose, PanelLeftOpen, Receipt, UserCircle, Users, X } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { cachedGet, sistema } from '../../utils/api'
import BuscaGlobal from '../BuscaGlobal'
import ThemeToggle from '../ThemeToggle'
import BrandMark from './BrandMark'
import OperationalExperience from '../OperationalExperience'
import TableColumnManager from '../TableColumnManager'
import { APP_VERSION, completeCacheKey } from '../../utils/operational'
import RouteContentFallback from '../ui/RouteContentFallback'
import ChangelogModal from '../ChangelogModal'
import { lazyWithRetry } from '../../utils/lazyWithRetry'
import MobileBottomBar from './MobileBottomBar'

const CrmCadenceAlarm = lazyWithRetry(() => import('../crm/CrmCadenceAlarm'))

const notificationVisual = tipo => ({
  chamado_sem_tecnico: { icon: ClipboardList, tone: 'brand' },
  chamado_atribuido: { icon: ClipboardList, tone: 'brand' },
  chamado_reaberto: { icon: ClipboardList, tone: 'warning' },
  chamado_critico: { icon: AlertTriangle, tone: 'critical' },
  aguardando_cliente: { icon: ClipboardList, tone: 'warning' },
  avaliacao_pendente: { icon: ClipboardList, tone: 'success' },
  sla_proximo: { icon: AlertTriangle, tone: 'warning' },
  sla_vencido: { icon: AlertTriangle, tone: 'critical' },
  sla_risco: { icon: AlertTriangle, tone: 'warning' },
  sla_violado: { icon: AlertTriangle, tone: 'critical' },
  fatura_vencida: { icon: Receipt, tone: 'critical' },
  fatura_disponivel: { icon: Receipt, tone: 'brand' },
  fatura_contestada: { icon: Receipt, tone: 'warning' },
  pagamento_confirmado: { icon: Receipt, tone: 'success' },
  gateway_erro: { icon: AlertTriangle, tone: 'critical' },
  gateway_alerta: { icon: AlertTriangle, tone: 'warning' },
  crm_atividade_vencida: { icon: Bell, tone: 'warning' },
  crm_toque_vencido: { icon: Bell, tone: 'warning' },
  crm_orcamento_resposta: { icon: Bell, tone: 'brand' },
  alerta_escalonado: { icon: AlertTriangle, tone: 'critical' },
  notificacao: { icon: Bell, tone: 'brand' },
  chamado: { icon: ClipboardList, tone: 'brand' },
  fatura: { icon: Receipt, tone: 'critical' },
}[tipo] || { icon: Bell, tone: 'brand' })

function NavItems({ items, pathname, onNavigate, collapsed }) {
  const itemPath = value => value?.split('?')[0]
  const [expandedMenus, setExpandedMenus] = useState({})
  const [hoveredMenu, setHoveredMenu] = useState(null)
  const [flyoutTop, setFlyoutTop] = useState(null)
  const leaveTimeoutRef = useRef(null)

  const flyoutRef = useRef(null)

  const toggleMenu = (key) => {
    setExpandedMenus(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleMouseEnter = (key, event, childrenList = []) => {
    if (!collapsed) return
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    const rect = event.currentTarget.getBoundingClientRect()
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const childCount = childrenList?.length || 1
    const estimatedFlyoutHeight = 36 + (childCount * 34) + 18
    let top = rect.top
    if (top + estimatedFlyoutHeight > windowHeight - 16) {
      top = Math.max(16, windowHeight - estimatedFlyoutHeight - 16)
    }
    setFlyoutTop(top)
    setHoveredMenu(key)
  }

  const handleMouseLeave = () => {
    if (!collapsed) return
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current)
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredMenu(null)
      setFlyoutTop(null)
    }, 220)
  }

  useEffect(() => {
    if (flyoutRef.current && flyoutTop !== null) {
      const flyoutRect = flyoutRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight || document.documentElement.clientHeight
      if (flyoutRect.bottom > windowHeight - 12) {
        const adjustedTop = Math.max(12, windowHeight - flyoutRect.height - 12)
        if (adjustedTop !== flyoutTop) {
          setFlyoutTop(adjustedTop)
        }
      }
    }
  }, [hoveredMenu, flyoutTop])

  return items.map(({ to, icon: Icon, label, children, section='Operação', alwaysExpanded=false }, index) => {
    const currentPath = itemPath(to)
    const isChildActive = children?.some(item => pathname === itemPath(item.to) || (itemPath(item.to) !== '/' && pathname.startsWith(itemPath(item.to))))
    const isExactParentActive = pathname === currentPath && !children?.length
    const active = isExactParentActive || isChildActive
    const prevSection = index > 0 ? (items[index - 1].section || 'Operação') : null
    const showSection = section !== prevSection
    const hasChildren = Boolean(children?.length)
    const isExpanded = expandedMenus[to] ?? (isChildActive || alwaysExpanded)
    const isHovered = hoveredMenu === to

    return (
      <div
        key={to}
        className={`app-nav-group ${hasChildren ? 'has-subnav' : ''}`}
        onMouseEnter={(e) => hasChildren && handleMouseEnter(to, e, children)}
        onMouseLeave={() => hasChildren && handleMouseLeave()}
      >
        {showSection ? <p className="app-nav-section" aria-hidden={collapsed ? 'true' : undefined}>{section}</p> : null}
        
        {hasChildren ? (
          <button
            type="button"
            onClick={() => !collapsed && toggleMenu(to)}
            title={collapsed ? label : undefined}
            aria-current={active ? 'page' : undefined}
            aria-expanded={!collapsed ? isExpanded : undefined}
            aria-controls={!collapsed ? `subnav-${to.replace(/[^a-zA-Z0-9_-]/g, '')}` : undefined}
            className={`app-nav-link ${collapsed ? 'justify-center !px-0' : 'w-full text-left flex items-center justify-between'}`}
            style={{ cursor: 'pointer' }}
          >
            <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
              <Icon aria-hidden="true" />
              {!collapsed && <span>{label}</span>}
            </div>
            {!collapsed && (
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={`text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              />
            )}
          </button>
        ) : (
          <Link
            to={to}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            aria-current={active ? 'page' : undefined}
            className={`app-nav-link ${collapsed ? 'justify-center !px-0' : ''}`}
          >
            <Icon aria-hidden="true" />
            {!collapsed && <span>{label}</span>}
          </Link>
        )}

        {!collapsed && hasChildren && isExpanded ? (
          <div className="app-subnav" id={`subnav-${to.replace(/[^a-zA-Z0-9_-]/g, '')}`}>
            {children.map(({ to: childTo, icon: ChildIcon, label: childLabel }) => {
              const childActive = pathname === itemPath(childTo)
              return (
                <Link key={childTo} to={childTo} onClick={onNavigate} title={childLabel} aria-current={childActive ? 'page' : undefined} className="app-subnav-link">
                  <ChildIcon aria-hidden="true" />
                  <span>{childLabel}</span>
                </Link>
              )
            })}
          </div>
        ) : null}

        {collapsed && hasChildren && isHovered && flyoutTop !== null ? (
          <div
            ref={flyoutRef}
            className="sidebar-flyout-menu animate-in fade-in zoom-in-95 duration-150"
            style={{
              top: `${flyoutTop}px`,
              maxHeight: `calc(100dvh - ${flyoutTop + 16}px)`
            }}
          >
            <div className="sidebar-flyout-header">
              <strong>{label}</strong>
            </div>
            <div className="sidebar-flyout-links">
              {children.map(({ to: childTo, icon: ChildIcon, label: childLabel }) => {
                const childActive = pathname === itemPath(childTo)
                return (
                  <Link
                    key={childTo}
                    to={childTo}
                    onClick={() => {
                      setHoveredMenu(null)
                      onNavigate()
                    }}
                    aria-current={childActive ? 'page' : undefined}
                    className="sidebar-flyout-link"
                  >
                    <ChildIcon size={15} aria-hidden="true" />
                    <span>{childLabel}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
    )
  })
}

function NotificationCenter() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const load = (force=false) => cachedGet(completeCacheKey('notifications',{userId:user.id,organizationId:user.cliente_id}), () => sistema.notificacoes(), { force }).then(response => { setData(response.data); setError('') }).catch(() => setError('Não foi possível carregar as notificações.'))
  useEffect(() => { load() }, [])
  useEffect(() => {
    const handleFocus = () => load(true)
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])
  useEffect(() => {
    if (!open) return undefined
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])
  const items = data?.notificacoes || data?.pendencias || []

  const markAll = async () => {
    const previous=data;setData(current=>({...current,notificacoes:[],pendencias:[]}))
    try { await sistema.marcarTodasNotificacoes(); load(true) } catch { setData(previous) }
  }
  const markOne = async item => {
    setOpen(false)
    if (!item.notificacao_id) return
    const previous=data;setData(current=>({...current,notificacoes:(current?.notificacoes||[]).filter(x=>x.key!==item.key),pendencias:(current?.pendencias||[]).filter(x=>x.key!==item.key)}))
    try { await sistema.marcarNotificacao(item.notificacao_id); load(true) } catch { setData(previous) }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(value => !value)} className="topbar-icon-button" aria-label="Notificações" aria-expanded={open}>
        <Bell size={18} />
        {items.length ? <span className="notification-count">{items.length > 99 ? '99+' : items.length}</span> : null}
      </button>
      {open && createPortal(
        <div className="notification-layer">
          <button className="notification-backdrop" aria-label="Fechar notificações" onClick={() => setOpen(false)} />
          <section className="notification-panel" role="dialog" aria-label="Notificações">
            <header>
              <div><strong>Notificações</strong><small>{items.length} ativas</small></div>
              <button type="button" onClick={markAll} disabled={!items.length}>Marcar todas como lidas</button>
            </header>
            {!data && !error ? <div className="notification-skeleton" role="status" aria-label="Carregando notificações">{[1,2,3].map(item=><span key={item} className="aden-skeleton"/>)}</div> : null}
            {error ? <div className="notification-empty" role="alert"><span>{error}</span><button type="button" onClick={() => load(true)}>Tentar novamente</button></div> : null}
            {data && !items.length && !error ? <p className="notification-empty">Nenhuma notificação no momento</p> : null}
            {items.map(item => {
              const { icon: Icon, tone } = notificationVisual(item.tipo)
              return (
                <Link key={item.key} to={item.to || '/'} onClick={() => markOne(item)} className="notification-item">
                  <span className={`tone-icon tone-icon--${tone}`}><Icon size={16} /></span>
                  <span><strong>{item.titulo}</strong><small>{item.descricao}</small></span>
                </Link>
              )
            })}
          </section>
        </div>, document.body
      )}
    </>
  )
}

export default function AppShell({ children, navItems, roleLabel, showSearch = true, showNotifications = false, contentWidth = 'wide' }) {
  const location = useLocation()
  const { pathname } = location
  const navigationType = useNavigationType()
  const { user, logout, features } = useAuth()
  const [accountOpen, setAccountOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [changelogOpen, setChangelogOpen] = useState(false)
  const collapseKey = `aden:shell:collapsed:${user?.id || 'anonymous'}`
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(collapseKey) === 'true')
  const navTriggerRef = useRef(null)
  const accountPanelRef = useRef(null)
  const accountTriggerRef = useRef(null)
  const closeRef = useRef(null)
  const sidebarRef = useRef(null)
  const routeLabel = useMemo(() => {
    const allItems = navItems.flatMap(item => [item, ...(item.children || [])])
    const exact = allItems.find(item => item.to === pathname)
    if (exact) return exact.label
    if (pathname.startsWith('/chamados/')) return 'Detalhe do chamado'
    if (pathname.startsWith('/clientes/')) return 'Detalhe do cliente'
    if (pathname.startsWith('/contratos/')) return 'Detalhe do contrato'
    if (pathname.startsWith('/fechamento/')) return 'Detalhe da fatura'
    if (pathname.startsWith('/crm/oportunidade/')) return 'Oportunidade'
    if (pathname === '/perfil') return 'Meu perfil'
    if (pathname.startsWith('/orcamento/')) return 'Orçamento'
    return 'Página atual'
  }, [navItems, pathname])

  useEffect(() => {
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        if (navOpen) { setNavOpen(false); navTriggerRef.current?.focus() }
        if (accountOpen) { setAccountOpen(false); accountTriggerRef.current?.focus() }
        return
      }
      if (event.key === 'Tab' && navOpen) {
        const focusable = [...(sidebarRef.current?.querySelectorAll('a[href],button:not([disabled])') || [])]
        const first = focusable[0]; const last = focusable.at(-1)
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [accountOpen, navOpen])

  useEffect(() => {
    if (navOpen) queueMicrotask(() => closeRef.current?.focus())
  }, [navOpen])

  const isFirstMountRef = useRef(true)
  const previousPathnameRef = useRef(pathname)

  useEffect(() => {
    const isFirstMount = isFirstMountRef.current
    if (isFirstMount) {
      isFirstMountRef.current = false
    } else if (previousPathnameRef.current === pathname) {
      return
    }
    previousPathnameRef.current = pathname

    let observer
    let stopObserver
    const frame = requestAnimationFrame(() => {
      setNavOpen(false)
      setAccountOpen(false)
      const main = document.getElementById('conteudo-principal')
      if (navigationType !== 'POP') main?.scrollTo?.({ top: 0, behavior: 'auto' })
      const focusHeading = () => {
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
          observer?.disconnect()
          if (stopObserver) clearTimeout(stopObserver)
          return true
        }
        const heading = main?.querySelector('h1')
        if (!heading) return false
        heading.tabIndex = -1
        heading.focus({ preventScroll: true })
        observer?.disconnect()
        if (stopObserver) clearTimeout(stopObserver)
        return true
      }
      if (!focusHeading() && main) {
        main.focus({ preventScroll: true })
        observer = new MutationObserver(focusHeading)
        observer.observe(main, { childList: true, subtree: true })
        stopObserver = setTimeout(() => observer?.disconnect(), 2000)
      }
    })
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); if (stopObserver) clearTimeout(stopObserver) }
  }, [navigationType, pathname])

  useEffect(() => { localStorage.setItem(collapseKey, String(collapsed)) }, [collapseKey, collapsed])

  useEffect(() => {
    if (!accountOpen) return
    const closeOutside = event => {
      if (!accountPanelRef.current?.contains(event.target)) setAccountOpen(false)
    }
    document.addEventListener('pointerdown', closeOutside)
    return () => document.removeEventListener('pointerdown', closeOutside)
  }, [accountOpen])

  useEffect(() => { document.title = `${routeLabel} · Aden Technology` }, [routeLabel])
  if (!user) return <Navigate to="/login" replace />
  const initials = user.nome?.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase()
  const template = pathname === '/' || pathname === '/hoje'
    ? 'command'
    : pathname === '/perfil' || pathname === '/configuracoes'
      ? 'settings'
      : /^\/(chamados|clientes|contratos|fechamento)\/[^/]+$/.test(pathname)
        ? 'detail'
        : 'collection'
  const detailCollection = pathname.startsWith('/chamados/') ? { to:'/chamados', label:'Chamados' }
    : pathname.startsWith('/clientes/') ? { to:'/clientes', label:'Clientes' }
      : pathname.startsWith('/contratos/') ? { to:'/clientes', label:'Clientes' }
        : pathname.startsWith('/fechamento/') ? { to:'/financeiro', label:'Financeiro' }
          : pathname.startsWith('/crm/oportunidade/') ? { to:'/crm', label:'CRM' }
            : pathname.startsWith('/atendimento/') ? { to:'/atendimento', label:'Atendimento WhatsApp' } : null

  return (
    <div className={`app-shell aden-board-shell ${collapsed ? 'is-sidebar-collapsed' : ''}`} data-route={pathname} data-template={template}>
      <Suspense fallback={null}><CrmCadenceAlarm /></Suspense>
      <button type="button" className={`mobile-nav-backdrop ${navOpen ? 'is-open' : ''}`} aria-label="Fechar menu" onClick={() => { setNavOpen(false); navTriggerRef.current?.focus() }} />
      <aside ref={sidebarRef} className={`app-sidebar ${navOpen ? 'is-open' : ''}`} aria-label="Menu principal">
        <div className="app-sidebar-brand"><BrandMark /></div>
        <button ref={closeRef} type="button" className="mobile-nav-close touch-target-44 min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => { setNavOpen(false); navTriggerRef.current?.focus() }} aria-label="Fechar menu"><X size={20}/></button>
        <nav id="navegacao-principal" className="app-nav" aria-label="Navegação principal"><NavItems items={navItems} pathname={pathname} collapsed={collapsed} onNavigate={() => setNavOpen(false)} /></nav>
        <button type="button" className="sidebar-collapse hidden md:flex" onClick={() => setCollapsed(value => !value)} aria-label={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'} title={collapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}>{collapsed ? <PanelLeftOpen aria-hidden="true"/> : <PanelLeftClose aria-hidden="true"/>}<span>{collapsed ? 'Expandir' : 'Recolher'}</span></button>
        {!collapsed && (
          <div className="operational-footer"><span>v{APP_VERSION}</span><span className="environment-badge">{import.meta.env.MODE === 'production' ? 'Produção' : 'Desenvolvimento'}</span></div>
        )}
        <div className="mobile-sidebar-utilities" aria-label="Recursos pessoais">
          {features.experiencia?.enabled ? <>
            <button type="button" onClick={() => { setNavOpen(false); window.dispatchEvent(new CustomEvent('aden:open-experience', { detail:'help' })) }}><BookOpen size={17}/><span>Ajuda e atalhos</span></button>
            <button type="button" onClick={() => { setNavOpen(false); window.dispatchEvent(new CustomEvent('aden:open-experience', { detail:'feedback' })) }}><MessageSquare size={17}/><span>Enviar sugestão</span></button>
          </> : null}
          <button type="button" onClick={() => { setNavOpen(false); setChangelogOpen(true) }}><Megaphone size={17}/><span>Novidades</span></button>
          <Link to="/perfil" onClick={() => setNavOpen(false)}><UserCircle size={17}/><span>Aparência e perfil</span></Link>
        </div>
        <div ref={accountPanelRef} className="app-user-panel">
          <button ref={accountTriggerRef} type="button" onClick={() => setAccountOpen(value => !value)} className="sidebar-account" aria-expanded={accountOpen} aria-controls="sidebar-account-menu">
            <span className="user-avatar">{initials}</span>
            <span className="user-copy"><strong>{user.nome}</strong><small>{roleLabel}</small></span>
            <ChevronDown size={14} className={accountOpen ? 'is-open' : ''} />
          </button>
          {accountOpen ? <div id="sidebar-account-menu" className="sidebar-account-menu">
            <Link to="/perfil" onClick={() => setAccountOpen(false)}><UserCircle size={15} />Meu perfil</Link>
            <button type="button" onClick={logout}><LogOut size={15} />Sair</button>
          </div> : null}
        </div>
      </aside>
      <div className="app-content">
        <header className="app-topbar">
          <button ref={navTriggerRef} type="button" className="mobile-nav-trigger hidden" onClick={() => setNavOpen(true)} aria-label="Abrir menu" aria-expanded={navOpen} aria-controls="navegacao-principal"><Menu size={20}/></button>
          <nav className="topbar-breadcrumb !hidden md:!flex" aria-label="Você está aqui">
            <Link to="/" className="topbar-breadcrumb-link hidden sm:inline">Aden</Link>
            <ChevronRight size={13} aria-hidden="true" className="topbar-breadcrumb-separator hidden sm:inline"/>
            {detailCollection ? (
              <>
                <Link to={detailCollection.to} state={location.state} className="topbar-breadcrumb-link">
                  {detailCollection.label}
                </Link>
                <ChevronRight size={13} aria-hidden="true" className="topbar-breadcrumb-separator hidden sm:inline"/>
              </>
            ) : null}
            <span aria-current="page" className="topbar-breadcrumb-current">{routeLabel}</span>
          </nav>
          <div className="topbar-search-slot">{showSearch ? <BuscaGlobal /> : null}</div>
          <div className="topbar-actions">
            <TableColumnManager pathname={pathname} userId={user.id}/>
            {features.experiencia?.enabled ? <OperationalExperience user={user} /> : null}
            <button type="button" className="topbar-action-btn" onClick={() => setChangelogOpen(true)} aria-label="Ver novidades"><Megaphone size={18}/></button>
            <ThemeToggle />
            {showNotifications ? <NotificationCenter /> : null}
          </div>
        </header>
        <main id="conteudo-principal" tabIndex="0" className="app-main" aria-label={routeLabel}>
          <span className="sr-only" role="status" aria-live="polite">Página atual: {routeLabel}</span>
          <div className={`app-main-inner app-main-inner--${contentWidth}`}>
            <Suspense fallback={<RouteContentFallback/>}>{children || <Outlet />}</Suspense>
          </div>
        </main>
      </div>
      <MobileBottomBar
        navItems={navItems}
        user={user}
        roleLabel={roleLabel}
        logout={logout}
        features={features}
        onOpenChangelog={() => setChangelogOpen(true)}
      />
      <ChangelogModal open={changelogOpen} onClose={() => setChangelogOpen(false)} />
    </div>
  )
}
